import React, {useContext, useEffect, useState} from 'react';
import MonacoEditor from '@monaco-editor/react';

import {useAnalytics} from '../../hooks';
import Button, {ButtonTypes} from '../ButtonNew';
import DropdownInput from '../DropdownInput';
import CodeEngine from './CodeEngine';
import SQLEngine from './SQLEngine';
import {ConsoleOutput} from './Console';
import {CourseLessonsContext} from '../CourseLessonsProvider';
import {toMonacoLanguage} from '../../util';

// Returns the correct 'engine' based on the lesson type
export const getEditor = (lesson) => {
  if (lesson?.codeProblem?.sqlCode) return SQLEngine;
  if (lesson?.codeProblem) return CodeEngine;
  return null;
};

export default function EditorEmbed() {
  const {currentLesson: lesson} = useContext(CourseLessonsContext);
  const {tracker} = useAnalytics();
  const [hasEdited, setHasEdited] = useState(false);

  const onEditCode = ({language, ...rest}) => {
    if (!hasEdited) {
      tracker.codeEditorEditCode({
        type: 'lesson', lesson: lesson.slug, language, ...rest,
      });
      setHasEdited(true);
    }
  };

  const onRunCode = ({language, ...rest}) => {
    tracker.codeEditorRunCode({
      type: 'lesson', lesson: lesson.slug, language, ...rest,
    });
  };

  const onRunTests = ({language, ...rest}) => {
    tracker.codeEditorRunTests({
      type: 'lesson', lesson: lesson.slug, language, ...rest,
    });
  };

  const onPassTests = ({language, ...rest}) => {
    tracker.codeEditorPassedTests({
      type: 'lesson', lesson: lesson.slug, language, ...rest,
    });
  };

  useEffect(() => {
    if (!lesson) return;
    tracker.codeEditorView({type: 'lesson', lesson: lesson.slug});
    setHasEdited(false);
  }, [lesson]);

  if (!lesson || !lesson?.codeProblem) {
    return null;
  }

  // Returns correct editor type based on lesson
  const Editor = getEditor(lesson);

  if (!Editor || !lesson?.codeProblem) return null;

  return (
    <Editor
      code={lesson.codeProblem}
      draftSlug={lesson.slug}
      key={lesson.slug}
      onEditCode={onEditCode}
      onRunCode={onRunCode}
      onRunTests={onRunTests}
      onPassTests={onPassTests}
    >
      {({
        content,
        languageOptions,
        activeLanguage,
        isRunning,
        isTesting,
        hasTests,
        consoleResults,
        onChange,
        onSubmit,
        onChangeLanguage,
      }) => (
        <div className="my-4 p-4 flex flex-col items-start justify-start shadow rounded-lg border border-gray-200">
          <DropdownInput
            options={languageOptions}
            value={activeLanguage}
            onChange={onChangeLanguage}
            showReset={false}
            highlightActive={false}
            className="mr-2"
            native
          />
          <div className="w-full mt-4">
            <MonacoEditor
              height="10em"
              language={toMonacoLanguage(activeLanguage)}
              value={content}
              onChange={onChange}
              options={{
                minimap: {
                  enabled: false,
                },
                automaticLayout: true,
                lineNumbers: 'off',
                padding: {top: 8},
                lineDecorationsWidth: 0,
                scrollbar: {
                  vertical: 'hidden',
                },
                fontSize: '14px',
                renderLineHighlight: 'none',
              }}
            />
            <div className="flex justify-between items-end mt-1">
              <div>
                <Button
                  onClick={onSubmit}
                  disabled={isRunning || isTesting}
                  loading={isRunning}
                  buttonType={hasTests ? ButtonTypes.SECONDARY : ButtonTypes.PRIMARY}
                  className="mr-2"
                >
                  Run code
                  {!isRunning && <span className="ml-2 text-xs font-regular opacity-75">⌘↩</span>}
                </Button>
              </div>
            </div>
            {consoleResults && (
              <div className="-mx-4 -mb-4">
                <ConsoleOutput results={consoleResults} />
              </div>
            )}
          </div>
        </div>
      )}
    </Editor>
  );
}
